/**
 * Columns drawer — surfaces a `columnCount` override on sections that
 * actually contain a multi-item `cs-*-group > .cs-item` layout.
 *
 * Visibility is gated upstream by `detectMultiItemGroups(section)`; the
 * caller in `property-panel.js` only mounts the row when that returns
 * non-null. The drawer body itself is just a segmented chip strip:
 *
 *   ┌───────────────────────────────────────────────┐
 *   │  Columns                                      │
 *   │  [1] [2] [3] [4] [5] [6]   • [⟲ Reset]        │
 *   └───────────────────────────────────────────────┘
 *
 * Range = `1..min(itemCount, 6)`. When `itemCount > 6`, the original count
 * appears as a trailing chip so "snap back to the source layout" is one
 * click away. The Reset chip deletes the `columnCount` override.
 *
 * Every commit flows through `applySectionOverride` so the existing
 * five-emission contract (store.update + theme:override-changed +
 * glass:spacing-changed + query-section-positions + querySectionSpacing)
 * fires unchanged.
 */

import {
  applySectionOverride,
  applySectionOverridesBulk,
  summarizeOverrideAcross
} from './apply-section-override.js';
import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { detectMultiItemGroups } from '../../editing/section-group-detection.js';
import { escapeHTML as escapeHtml } from '../../shared/html-escape.js';

const MAX_RANGE = 6;

function buildChipValues(itemCount) {
  const cap = Math.min(itemCount, MAX_RANGE);
  const out = [];
  for (let i = 1; i <= cap; i++) out.push(i);
  if (itemCount > MAX_RANGE && !out.includes(itemCount)) out.push(itemCount);
  return out;
}

function readOverride(section) {
  const raw = section?.overrides?.columnCount;
  if (raw == null || raw === '') return null;
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

/**
 * Mount the Columns drawer body into `container` and wire chip clicks.
 *
 * Multi-select (16B2): the row only mounts when EVERY selected section's
 * parsed group signature (groupSelector + itemCount) matches — equal
 * catalogKey is not proof — so the drawer can honestly drive them all with
 * one absolute value. A mixed columnCount renders no active chip.
 *
 * @param {HTMLElement} container - drawer body
 * @param {{ instanceId: string, instanceIds?: string[], leadId?: string }} ctx
 * @returns {() => void} teardown
 */
export function renderColumnsDrawer(container, { instanceId, instanceIds, leadId }) {
  if (!container || !instanceId) {
    console.warn('[columns-drawer] missing container/instanceId');
    return () => {};
  }

  const targets =
    Array.isArray(instanceIds) && instanceIds.length > 1 ? instanceIds.slice() : [instanceId];
  const multi = targets.length > 1;
  const lead = leadId || instanceId;

  function resolveSection() {
    const page = store.getActivePage();
    if (!page || !Array.isArray(page.sections)) return null;
    return page.sections.find((s) => s.instanceId === (multi ? lead : instanceId)) || null;
  }

  function resolveTargetSections() {
    const page = store.getActivePage();
    if (!page || !Array.isArray(page.sections)) return [];
    const byId = new Map(page.sections.map((s) => [s.instanceId, s]));
    return targets.map((id) => byId.get(id)).filter(Boolean);
  }

  function applyColumns(value, source) {
    if (multi) {
      applySectionOverridesBulk(
        targets,
        { columnCount: value },
        {
          source,
          lead,
          transaction: 'auto'
        }
      );
      return;
    }
    applySectionOverride(instanceId, 'columnCount', value, { source });
  }

  function detect() {
    return detectMultiItemGroups(resolveSection());
  }

  function render() {
    const section = resolveSection();
    const group = detect();
    const mixed =
      multi && summarizeOverrideAcross(resolveTargetSections(), 'columnCount').state === 'mixed';
    const current = mixed ? null : readOverride(section);
    if (!group) {
      container.innerHTML = `
        <div class="pb-col-drawer">
          <p class="pb-col-empty">This section has no multi-item group to reflow.</p>
        </div>
      `;
      return;
    }

    const chips = buildChipValues(group.itemCount);
    const isOverridden = multi
      ? resolveTargetSections().some((s) => readOverride(s) != null)
      : current != null;
    // Mixed values across the selection: no chip reads active (`effective`
    // null matches nothing); choosing one applies to all.
    const effective = mixed ? null : (current ?? group.itemCount);

    container.innerHTML = `
      <div class="pb-col-drawer" data-columns-drawer>
        <p class="pb-col-hint">
          Reflows <code>${escapeHtml(group.groupSelector)}</code> at desktop widths.
          The section ships with ${group.itemCount} item${group.itemCount === 1 ? '' : 's'}.
        </p>
        <div class="pb-col-chips" role="group" aria-label="Columns">
          ${chips
            .map(
              (n) => `
            <button type="button" class="pb-col-chip${n === effective ? ' pb-col-chip-active' : ''}"
                    data-col-value="${n}"
                    aria-pressed="${n === effective ? 'true' : 'false'}">${n}</button>
          `
            )
            .join('')}
          <button type="button" class="pb-col-reset${isOverridden ? '' : ' pb-col-reset-disabled'}"
                  data-col-reset
                  ${isOverridden ? '' : 'disabled aria-disabled="true"'}
                  aria-label="Reset column override">
            <span aria-hidden="true">⟲</span><span>Reset</span>
          </button>
        </div>
      </div>
    `;

    container.querySelectorAll('[data-col-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const n = Math.floor(Number(btn.getAttribute('data-col-value')));
        if (!Number.isFinite(n) || n < 1) return;
        // Apply the override (even when n matches the source itemCount) —
        // an explicit override is the user's recorded choice and survives
        // future section-html mutations.
        applyColumns(String(n), 'columns-drawer');
      });
    });

    const resetBtn = container.querySelector('[data-col-reset]');
    if (resetBtn && isOverridden) {
      resetBtn.addEventListener('click', () => {
        applyColumns('', 'columns-drawer-reset');
      });
    }
  }

  render();

  const onOverrideChanged = ({ instanceId: id, field } = {}) => {
    if (id && !targets.includes(id)) return;
    if (field && field !== 'columnCount') return;
    render();
  };
  const onStateChanged = () => render();
  eventBus.on('theme:override-changed', onOverrideChanged);
  eventBus.on('state-changed', onStateChanged);

  return function teardown() {
    eventBus.off('theme:override-changed', onOverrideChanged);
    eventBus.off('state-changed', onStateChanged);
  };
}
